import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, CheckCircle, Zap, ArrowLeft, DollarSign, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

// Generac generator images for rotation
const GENERAC_IMAGES = [
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/qjmkw6dm_GeneracGenerator-HouseShot.jpg',
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/n1tc9fzh_GeneracGenerator-HouseShot-2.jpg',
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/485id8h9_Generac-Generators-ProductShot.jpg',
  'https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/09aros2b_Generac-Generators-ProductShot-Large.jpg'
];

// Function to get random Generac image
const getRandomGeneracImage = () => {
  return GENERAC_IMAGES[Math.floor(Math.random() * GENERAC_IMAGES.length)];
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [generatorImage, setGeneratorImage] = useState(getRandomGeneracImage());
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await api.getServices();
        setServices(response.data);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
    // Set random generator image on mount
    setGeneratorImage(getRandomGeneracImage());
  }, []);

  const serviceImages = {
    'residential-electrical': 'https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg',
    'commercial-electrical': 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
    'ev-charger-installation': 'https://images.unsplash.com/photo-1765272088009-100c96a4cd4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxlbGVjdHJpYyUyMHZlaGljbGUlMjBjaGFyZ2luZyUyMHN0YXRpb24lMjBob21lfGVufDB8fHx8MTc3MTU3NzI3NXww&ixlib=rb-4.1.0&q=85',
    'panel-replacement': 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg',
    'generator-installation': generatorImage,
    'lighting-installation': 'https://images.pexels.com/photos/31737859/pexels-photo-31737859.jpeg',
    'electrical-planning-reports': 'https://images.pexels.com/photos/7359566/pexels-photo-7359566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'bc-hydro-power-smart': 'https://images.unsplash.com/photo-1764885517995-a4a4f940e1a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVsJTIwaW5zdGFsbGF0aW9uJTIwY29tbWVyY2lhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4MjYxMTYzNnww&ixlib=rb-4.1.0&q=85'
  };

  // Sort services per owner preference
  const serviceOrder = ['ev-charger-installation', 'panel-replacement', 'generator-installation', 'bc-hydro-power-smart', 'lighting-installation', 'electrical-planning-reports', 'residential-electrical', 'commercial-electrical'];
  const sortedServices = useMemo(() => [...services].sort((a, b) => {
    const aIndex = serviceOrder.indexOf(a.slug);
    const bIndex = serviceOrder.indexOf(b.slug);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  }), [services]);

  // City links for each service
  const SERVICE_CITY_LINKS = {
    'ev-charger-installation': { prefix: 'ev-charger-installation', cities: ['victoria', 'langford', 'saanich', 'nanaimo', 'sooke', 'duncan'] },
    'panel-replacement': { prefix: 'panel-upgrades', cities: ['victoria', 'langford', 'saanich', 'nanaimo', 'sooke', 'duncan'] },
    'generator-installation': { prefix: 'generator-installation', cities: ['victoria', 'langford', 'sooke', 'nanaimo', 'duncan'] },
    'bc-hydro-power-smart': { prefix: 'bc-hydro-power-smart', cities: ['victoria', 'langford', 'nanaimo', 'duncan'] },
    'lighting-installation': { prefix: 'lighting-installation', cities: ['victoria', 'langford', 'saanich', 'nanaimo'] },
    'electrical-planning-reports': { prefix: 'epr-inspection', cities: ['victoria', 'langford', 'saanich', 'nanaimo'] },
    'residential-electrical': { prefix: 'residential-electrician', cities: ['victoria', 'langford', 'sooke', 'duncan'] },
    'commercial-electrical': { prefix: 'commercial-electrician', cities: ['victoria', 'langford', 'nanaimo'] },
  };

  const cityNames = {
    'victoria': 'Victoria', 'langford': 'Langford', 'saanich': 'Saanich',
    'nanaimo': 'Nanaimo', 'sooke': 'Sooke', 'duncan': 'Duncan',
  };

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">Our Services</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
              Professional Electrical Services
            </h1>
            <p className="text-white/60 text-lg">
              Residential, commercial, and industrial electrical across Vancouver Island. What do you need done?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Menu */}
      <section className="py-16 bg-primary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {sortedServices.map((service, index) => {
              const cityInfo = SERVICE_CITY_LINKS[service.slug];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="bg-secondary-dark rounded-xl border border-white/5 overflow-hidden hover:border-electric-teal/20 transition-colors"
                  data-testid={`service-block-${service.slug}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <Link to={`/services/${service.slug}`} className="md:w-72 lg:w-80 flex-shrink-0">
                      <div className="relative aspect-[16/10] md:aspect-auto md:h-full overflow-hidden">
                        <img
                          src={serviceImages[service.slug] || service.image_url}
                          alt={service.title}
                          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-secondary-dark/50 hidden md:block" />
                      </div>
                    </Link>

                    {/* Content */}
                    <div className="flex-1 p-5 md:p-6">
                      <Link to={`/services/${service.slug}`} className="group">
                        <h2 className="text-white font-bold text-xl group-hover:text-electric-teal transition-colors flex items-center gap-2">
                          {service.title}
                          <ArrowRight className="w-4 h-4 text-electric-teal opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </h2>
                      </Link>
                      <p className="text-white/50 text-sm leading-relaxed mt-2 mb-4 max-w-2xl">
                        {service.short_description}
                      </p>

                      {/* Quick features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(service.features || []).slice(0, 4).map((feat) => (
                          <span key={feat} className="bg-white/5 text-white/50 text-xs px-2.5 py-1 rounded-full border border-white/5">
                            {feat.length > 40 ? feat.slice(0, 37) + '...' : feat}
                          </span>
                        ))}
                      </div>

                      {/* City Links */}
                      {cityInfo && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-white/30 text-xs">Available in:</span>
                          {cityInfo.cities.map(city => (
                            <Link key={city} to={`/${cityInfo.prefix}-${city}`}
                              className="text-electric-teal/70 hover:text-electric-teal text-xs font-medium transition-colors"
                            >
                              {cityNames[city]}
                            </Link>
                          ))}
                          <Link to={`/services/${service.slug}`}
                            className="text-white/30 hover:text-white/60 text-xs transition-colors ml-1"
                          >
                            + all areas
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-electric-teal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-primary-dark tracking-tight mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-primary-dark/70 text-lg mb-8">
            Don&apos;t see what you&apos;re looking for? Contact us for a custom quote on any electrical project.
          </p>
          <Link to="/book-estimate" data-testid="services-cta-btn">
            <Button className="bg-primary-dark text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-secondary-dark transition-all">
              <Zap className="w-5 h-5 mr-2" />
              Get Free Estimate
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatorImage] = useState(getRandomGeneracImage());
  const [openFaq, setOpenFaq] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const serviceImages = {
    'residential-electrical': 'https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg',
    'commercial-electrical': 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
    'ev-charger-installation': 'https://images.unsplash.com/photo-1765272088009-100c96a4cd4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxlbGVjdHJpYyUyMHZlaGljbGUlMjBjaGFyZ2luZyUyMHN0YXRpb24lMjBob21lfGVufDB8fHx8MTc3MTU3NzI3NXww&ixlib=rb-4.1.0&q=85',
    'panel-replacement': 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg',
    'generator-installation': generatorImage,
    'lighting-installation': 'https://images.pexels.com/photos/31737859/pexels-photo-31737859.jpeg',
    'bc-hydro-power-smart': 'https://images.unsplash.com/photo-1764885517995-a4a4f940e1a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVsJTIwaW5zdGFsbGF0aW9uJTIwY29tbWVyY2lhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4MjYxMTYzNnww&ixlib=rb-4.1.0&q=85'
  };

  useEffect(() => {
    const loadService = async () => {
      try {
        const response = await api.getService(slug);
        setService(response.data);
      } catch (error) {
        console.error('Error loading service:', error);
      } finally {
        setLoading(false);
      }
    };
    loadService();
  }, [slug]);

  // Set meta tags and inject FAQ schema
  useEffect(() => {
    if (!service) return;

    // Page title
    document.title = service.meta_title || `${service.title} | Island Coastal Electric`;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', service.meta_description || service.short_description);

    // FAQ Schema.org
    const faqs = service.faqs || [];
    if (faqs.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      let scriptTag = document.getElementById('faq-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'faq-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(faqSchema);
    }

    // Service Schema.org
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": service.title,
      "description": service.short_description,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Island Coastal Electric",
        "telephone": "778-960-2377",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2668 Sooke Road",
          "addressLocality": "Victoria",
          "addressRegion": "BC",
          "postalCode": "V9B 1Y6",
          "addressCountry": "CA"
        }
      },
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 48.4284, "longitude": -123.3656 },
        "geoRadius": "100000"
      }
    };
    let serviceScriptTag = document.getElementById('service-schema');
    if (!serviceScriptTag) {
      serviceScriptTag = document.createElement('script');
      serviceScriptTag.id = 'service-schema';
      serviceScriptTag.type = 'application/ld+json';
      document.head.appendChild(serviceScriptTag);
    }
    serviceScriptTag.textContent = JSON.stringify(serviceSchema);

    return () => {
      // Cleanup schemas on unmount
      document.getElementById('faq-schema')?.remove();
      document.getElementById('service-schema')?.remove();
      document.title = 'Island Coastal Electric';
    };
  }, [service]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32 flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Service Not Found</h1>
          <Link to="/services" className="text-electric-teal hover:underline">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const hasPricing = service.pricing_factors?.length > 0;
  const hasSteps = service.process_steps?.length > 0;
  const hasFaqs = service.faqs?.length > 0;

  return (
    <div className="min-h-screen bg-primary-dark pt-20" data-testid="service-detail-page">
      {/* Hero Section */}
      <section className="relative py-24" data-testid={`service-hero-${slug}`}>
        <div className="absolute inset-0">
          {(serviceImages[slug] || service.image_url) && (
            <img 
              src={serviceImages[slug] || service.image_url}
              alt={service.title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary-dark/90 to-primary-dark/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-electric-teal mb-6 hover:text-white transition-colors"
              data-testid="back-to-services"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-bold uppercase tracking-wider">Back to Services</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
              {service.title}
            </h1>
            <p className="text-white/70 text-lg leading-relaxed">
              {service.short_description}
            </p>
            <div className="mt-8">
              <Link to="/book-estimate">
                <Button className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 py-4 text-base hover:bg-white transition-all btn-glow" data-testid="hero-cta-btn">
                  <Zap className="w-4 h-4 mr-2" />
                  Get Free Estimate
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About + Sidebar */}
      <section className="py-16 md:py-24 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {/* Certification Badges */}
              {slug === 'bc-hydro-power-smart' && (
                <div className="flex items-center gap-5 mb-8 p-5 bg-white/5 rounded-lg border border-white/10" data-testid="bc-hydro-badge">
                  <div className="w-24 h-24 rounded-lg bg-white/10 border border-electric-teal/30 flex items-center justify-center p-2 flex-shrink-0">
                    <span className="text-electric-teal font-bold text-xs text-center leading-tight">BC Hydro<br/>Alliance of<br/>Energy<br/>Professionals</span>
                  </div>
                  <div>
                    <p className="text-white font-bold">BC Hydro Alliance of Energy Professionals</p>
                    <p className="text-electric-teal text-sm font-semibold">Commercial/Industrial Rated Member</p>
                    <p className="text-white/40 text-xs mt-1">Certified to deliver BC Hydro incentive programs</p>
                  </div>
                </div>
              )}
              {slug === 'ev-charger-installation' && (
                <div className="flex items-center gap-5 mb-8 p-5 bg-white/5 rounded-lg border border-white/10" data-testid="tesla-badge">
                  <img 
                    src="https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/vcyr36jn_tesla-certified-installer.jpg"
                    alt="Tesla Certified Installer - Wall Connector"
                    className="w-24 h-24 object-contain rounded-lg bg-white p-1"
                  />
                  <div>
                    <p className="text-white font-bold">Tesla Certified Installer</p>
                    <p className="text-electric-teal text-sm font-semibold">Wall Connector Certified</p>
                    <p className="text-white/40 text-xs mt-1">Factory-trained for Tesla Wall Connector installations</p>
                  </div>
                </div>
              )}
              {slug === 'generator-installation' && (
                <div className="flex items-center gap-5 mb-8 p-5 bg-white/5 rounded-lg border border-white/10" data-testid="generac-badge">
                  <img 
                    src="https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/40wcnfw6_Generac-Installer.png"
                    alt="Generac Certified Installer"
                    className="w-24 h-24 object-contain rounded-lg bg-white p-1"
                  />
                  <div>
                    <p className="text-white font-bold">Licensed Generac Installer</p>
                    <p className="text-electric-teal text-sm font-semibold">Certified Partner</p>
                    <p className="text-white/40 text-xs mt-1">Factory-trained for residential &amp; commercial generators</p>
                  </div>
                </div>
              )}
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">About This Service</h2>
              <div className="text-white/70 text-lg leading-relaxed space-y-4">
                {service.full_description?.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </div>
            
            <div>
              <div className="bg-primary-dark p-6 md:p-8 rounded-lg border border-white/10 sticky top-24">
                <h3 className="text-white font-bold text-lg mb-5">What&apos;s Included</h3>
                <ul className="space-y-3">
                  {service.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-electric-teal flex-shrink-0 mt-1" />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-6 pt-6 border-t border-white/10">
                  <Link to="/book-estimate" data-testid="service-book-btn">
                    <Button className="w-full bg-electric-teal text-primary-dark font-bold uppercase tracking-wider py-4 hover:bg-white transition-all btn-glow">
                      <Zap className="w-4 h-4 mr-2" />
                      Book Free Estimate
                    </Button>
                  </Link>
                  <p className="text-white/30 text-xs text-center mt-3">No obligation. Same-day quotes available.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works / Process Steps */}
      {hasSteps && (
        <section className="py-16 md:py-24 bg-primary-dark" data-testid="process-steps-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">Our Process</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">How It Works</h2>
            </div>
            <div className="grid md:grid-cols-5 gap-4 md:gap-2">
              {service.process_steps.sort((a, b) => a.step - b.step).map((step) => (
                <motion.div
                  key={`step-${step.step}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: step.step * 0.1 }}
                  className="relative"
                >
                  <div className="bg-secondary-dark rounded-lg p-5 md:p-6 border border-white/5 hover:border-electric-teal/30 transition-colors h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 rounded-full bg-electric-teal/10 border border-electric-teal/30 flex items-center justify-center text-electric-teal font-bold text-sm flex-shrink-0">
                        {step.step}
                      </span>
                      {step.step < service.process_steps.length && (
                        <div className="hidden md:block flex-1 h-px bg-white/10" />
                      )}
                    </div>
                    <h3 className="text-white font-bold text-sm md:text-base mb-2">{step.title}</h3>
                    <p className="text-white/50 text-xs md:text-sm leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Factors */}
      {hasPricing && (
        <section className="py-16 md:py-24 bg-secondary-dark" data-testid="pricing-factors-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">Transparent Pricing</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">What Affects the Cost</h2>
              <p className="text-white/50 mt-4 max-w-2xl mx-auto">Every project is different. Here are the main factors that determine your quote — no surprises, no hidden fees.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.pricing_factors.map((factor, idx) => (
                <motion.div
                  key={`pf-${factor.title}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="bg-primary-dark rounded-lg p-6 border border-white/5 hover:border-electric-teal/20 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <DollarSign className="w-5 h-5 text-electric-teal flex-shrink-0" />
                    <h3 className="text-white font-bold">{factor.title}</h3>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">{factor.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {hasFaqs && (
        <section className="py-16 md:py-24 bg-primary-dark" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">Common Questions</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-3">
              {service.faqs.map((faq, index) => (
                <motion.div
                  key={`faq-${faq.question.slice(0, 20)}-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-secondary-dark rounded-lg border border-white/5 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left group"
                    data-testid={`faq-toggle-${index}`}
                    aria-expanded={openFaq === index}
                  >
                    <span className="text-white font-medium pr-4 group-hover:text-electric-teal transition-colors text-sm md:text-base">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-electric-teal flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white/40 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 bg-electric-teal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-dark tracking-tight mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-primary-dark/70 text-lg mb-8 max-w-xl mx-auto">
            Book a free estimate and one of our certified electricians will visit your property to provide a detailed, no-obligation quote.
          </p>
          <Link to="/book-estimate" data-testid="bottom-cta-btn">
            <Button className="bg-primary-dark text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-secondary-dark transition-all">
              <Zap className="w-5 h-5 mr-2" />
              Book Free Estimate
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export { ServicesPage, ServiceDetailPage };
export default ServicesPage;
